import { pickVideoMailMimeType, shouldUploadTake, toUploadContentType } from './localCapture';
/**
 * Bitrates for a take. Without them Chrome records video at about 2.5 Mbps. At
 * these values a 180 s take is about 37 MB, well under the 64 MB upload limit
 * in vrs-server/routes/voicemail.js and deploy/nginx.conf.
 */
export const VIDEOMAIL_VIDEO_BITS_PER_SECOND = 1500000;
export const VIDEOMAIL_AUDIO_BITS_PER_SECOND = 128000;
function getRecorderConstructor() {
    return globalThis.MediaRecorder;
}
/**
 * Whether this take has to be captured by the server-side recorder.
 *
 * The native app has no MediaRecorder. Since device recording it records on
 * its own camera screen and never opens a room for the take
 * (deviceRecorder.native.ts, VideoMailRecorderScreen). It still opens the
 * room, and Jibri records it, in a build from before device recording and
 * while a phone call holds the microphone. This test also covers a browser
 * too old to have a media recorder.
 *
 * @returns {boolean} True when the server-side recorder must be used.
 */
export function usesServerRecorder() {
    return !getRecorderConstructor();
}
/**
 * Starts recording the given camera and microphone stream.
 *
 * @param {IStartOptions} options - The stream and its duration limit.
 * @returns {IVideoMailCapture} A handle that ends the take.
 */
export function startVideoMailCapture({ stream, maxDurationSeconds, onAutoStop }) {
    const Recorder = getRecorderConstructor();
    if (!Recorder) {
        throw new Error('This browser cannot record video. Try Chrome, Edge, Firefox or Safari.');
    }
    const mimeType = pickVideoMailMimeType(type => Recorder.isTypeSupported(type));
    if (!mimeType) {
        throw new Error('This browser cannot record a format we can store. Try a different browser.');
    }
    const chunks = [];
    const recorder = new Recorder(stream, {
        audioBitsPerSecond: VIDEOMAIL_AUDIO_BITS_PER_SECOND,
        mimeType,
        videoBitsPerSecond: VIDEOMAIL_VIDEO_BITS_PER_SECOND
    });
    const startedAt = Date.now();
    let limitTimer;
    recorder.ondataavailable = event => {
        if (event.data?.size) {
            chunks.push(event.data);
        }
    };
    // A timeslice means a lost tab still leaves usable chunks behind, and it
    // keeps a long take from living in one growing buffer.
    recorder.start(1000);
    if (maxDurationSeconds && maxDurationSeconds > 0) {
        limitTimer = setTimeout(() => {
            if (recorder.state !== 'inactive') {
                recorder.stop();
                onAutoStop?.();
            }
        }, maxDurationSeconds * 1000);
    }
    return {
        stop() {
            return new Promise((resolve, reject) => {
                if (limitTimer) {
                    clearTimeout(limitTimer);
                }
                const finish = () => {
                    const BlobCtor = globalThis.Blob;
                    const blob = new BlobCtor(chunks, { type: recorder.mimeType || mimeType });
                    const decision = shouldUploadTake({ size: blob.size, mimeType: blob.type });
                    if (!decision.upload) {
                        reject(new Error(decision.reason));
                        return;
                    }
                    resolve({
                        blob,
                        contentType: toUploadContentType(blob.type),
                        durationSeconds: Math.max(0, Math.round((Date.now() - startedAt) / 1000))
                    });
                };
                recorder.onerror = () => reject(new Error('The recording stopped unexpectedly. Please record again.'));
                if (recorder.state === 'inactive') {
                    finish();
                    return;
                }
                recorder.onstop = finish;
                recorder.stop();
            });
        }
    };
}
